import { FormEvent, useState } from 'react';
import type {
  BasePropertyProps,
  ErrorMessage,
  PropertyJSON,
  RecordJSON,
} from 'adminjs';
import BaseTextInput from 'adminjs/lib/frontend/components/property-type/default-type/edit';
import { useNavigate } from 'react-router-dom';
import { ValidationError } from 'class-validator';

import { users } from '../api';
import { validationsToAdminErrors } from '../../../utils/admin-validation.utils';
import { emptyRecord } from '../../../components/utils/emptyRecord';
import { PATHS } from '../constants';

export type UserInputs = {
  email?: string;
};

export const useRequestPasswordReset = (): {
  onChange: (fieldName: string, value: string) => void;
  onSubmit: (e: FormEvent) => Promise<void>;
  record: RecordJSON;
  isLoading: boolean;
  onCancel: () => Promise<void>;
  fields: (PropertyJSON & {
    Component: React.ComponentType<BasePropertyProps>;
  })[];
} => {
  const [inputs, setInputs] = useState<UserInputs>({});
  const [errors, setErrors] = useState<Record<string, ErrorMessage>>({});
  const [isLoading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onChange = (fieldName: string, value: string): void => {
    clearFieldError(fieldName);
    setInputs((currentInputs) => ({ ...currentInputs, [fieldName]: value }));
  };

  const clearFieldError = (fieldName: string): void => {
    setErrors((previousErrors) => {
      delete previousErrors[fieldName];

      return previousErrors;
    });
  };

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      await users().requestPasswordReset(inputs);
      navigate(PATHS.REQUEST_PASSWORD_RESET_CONFIRMATION);
    } catch (e) {
      if (e.response?.status === 400) {
        setErrors(validationsToAdminErrors(e.response.data.message as ValidationError[]));
      } else if (e.response?.data?.message ?? e.message) {
        setErrors({ generalError: e.response?.data?.message ?? e.message });
      } else {
        setErrors({
          generalError: {
            message: 'Unexpected error happened. Try again later',
          },
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onCancel = async (): Promise<void> => {
    await navigate(PATHS.LOGIN);
  };

  const record = {
    ...emptyRecord,
    params: inputs,
    errors,
  } as RecordJSON;

  const fields = [
    {
      Component: BaseTextInput,
      label: 'Email',
      isRequired: true,
      propertyPath: 'email',
      path: 'email',
    },
  ] as (PropertyJSON & { Component: React.ComponentType<BasePropertyProps> })[];

  return { onChange, record, fields, onSubmit, isLoading, onCancel };
};
